import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect, useMemo, useContext, } from "react";
import { rankItem } from "@tanstack/match-sorter-utils";
import { flexRender, getCoreRowModel, useReactTable, getSortedRowModel, getPaginationRowModel, getFilteredRowModel, } from "@tanstack/react-table";
import { TableBody } from "./TableBody";
import { TableControls } from "./TableControls";
import { TableHeader } from "./TableHeader";
import { useTableColumns, useTableState } from "./hooks";
import { Spinner } from "../Spinner";
import { ThemeProviderContext } from "../ThemeProvider";
import { Pagination } from "../Pagination";
import classNames from "classnames";
export var RenderType;
(function (RenderType) {
    RenderType["text"] = "text";
    RenderType["currency"] = "currency";
    RenderType["date"] = "date";
})(RenderType || (RenderType = {}));
export const Table = ({ columns, data, disableSorting, isSelectable, pagination, page, totalPages, totalRows, onPageChange, onSelectionChange, onPageSizeChange, onSortChange, onFilterChange, onColumnVisibilityChange, sorting: externalSorting, pageSize: externalPageSize, activeFilters: externalActiveFilters, keywordFilter: externalKeywordFilter, rowSelection: externalRowSelection, onKeywordFilterChange, defaultPageSize = 50, getRowId, isLoading, filters, hasKeywordFilter, keywordFilterLabel, customControls, customKeywordFilterFn, rowActions, rowActionsLabel, exportOptions, columnVisibility: externalColumnVisibility, showColumnVisibility, emptyMessage, enableMultiRowSelection = true, }) => {
    const [isScrolling, setIsScrolling] = useState(false);
    const [internalSorting, setInternalSorting] = useState([]);
    const [internalRowSelection, setInternalRowSelection] = useState({});
    const [internalPageSize, setInternalPageSize] = useState(externalPageSize !== null && externalPageSize !== void 0 ? externalPageSize : defaultPageSize);
    const [internalColumnVisibility, setInternalColumnVisibility] = useState(externalColumnVisibility !== null && externalColumnVisibility !== void 0 ? externalColumnVisibility : columns.reduce((acc, column) => (Object.assign(Object.assign({}, acc), { [column.key]: true })), {}));
    const [internalActiveFilters, setInternalActiveFilters] = useState([]);
    const tableContainerRef = useRef(null);
    const [internalKeywordFilter, setInternalKeywordFilter] = useState("");
    const { translate: t, testAttribute } = useContext(ThemeProviderContext);
    const fuzzyFilter = (customGlobalFilterFn) => (row, columnId, value, addMeta) => {
        if (customGlobalFilterFn) {
            return customGlobalFilterFn(row.original, value);
        }
        const itemRank = rankItem(row.getValue(columnId), value, {
            threshold: 3,
        });
        addMeta({
            itemRank,
        });
        return itemRank.passed;
    };
    const keywordFilter = externalKeywordFilter !== null && externalKeywordFilter !== void 0 ? externalKeywordFilter : internalKeywordFilter;
    const activeFilters = externalActiveFilters !== null && externalActiveFilters !== void 0 ? externalActiveFilters : internalActiveFilters;
    const columnVisibility = externalColumnVisibility !== null && externalColumnVisibility !== void 0 ? externalColumnVisibility : internalColumnVisibility;
    const rowSelection = useMemo(() => externalRowSelection
        ? externalRowSelection.reduce((prev, current) => (Object.assign(Object.assign({}, prev), { [current]: true })), {})
        : internalRowSelection, [externalRowSelection, internalRowSelection]);
    const isSkeleton = !!isLoading && !data.length;
    const isEmpty = !isLoading && !data.length;
    const pageSize = externalPageSize !== null && externalPageSize !== void 0 ? externalPageSize : internalPageSize;
    const isPaginated = !!pagination;
    const isServerPaginated = pagination === "server";
    useEffect(() => {
        if (onSortChange) {
            onSortChange(internalSorting[0]
                ? {
                    id: internalSorting[0].id,
                    direction: internalSorting[0].desc ? "desc" : "asc",
                }
                : {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(internalSorting)]);
    const memoizedColumns = useTableColumns(columns, {
        disableSorting,
        isSelectable,
        filters,
        rowActions,
        rowActionsLabel,
    });
    const handleSelectionChange = (rowSelectionUpdater) => {
        if (onSelectionChange) {
            if (rowSelectionUpdater instanceof Function) {
                onSelectionChange(Object.keys(rowSelectionUpdater(rowSelection)));
            }
            else {
                onSelectionChange(Object.keys(rowSelectionUpdater));
            }
        }
        setInternalRowSelection(rowSelectionUpdater);
    };
    const tableState = useTableState({
        externalSorting,
        internalSorting,
        rowSelection,
        isPaginated,
        isSelectable,
        page,
        pageSize,
        activeFilters,
        keywordFilter,
        columnVisibility,
    });
    const table = useReactTable(Object.assign(Object.assign(Object.assign(Object.assign({ data, getRowId: (row, index) => {
            return getRowId ? getRowId(row) : index.toString();
        }, columns: memoizedColumns, initialState: Object.assign({}, (isPaginated && {
            pagination: {
                pageSize,
                pageIndex: page !== null && page !== void 0 ? page : 0,
            },
        })), globalFilterFn: fuzzyFilter(customKeywordFilterFn), state: tableState, manualPagination: isServerPaginated, manualSorting: isServerPaginated, manualFiltering: isServerPaginated, sortDescFirst: false }, (isSelectable && {
        enableRowSelection: true,
        enableMultiRowSelection,
        onRowSelectionChange: handleSelectionChange,
    })), { getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getFilteredRowModel: getFilteredRowModel() }), (isPaginated && { getPaginationRowModel: getPaginationRowModel() })), { onSortingChange: setInternalSorting }));
    const onTableScroll = () => {
        if (tableContainerRef.current) {
            const { scrollTop } = tableContainerRef.current;
            if (scrollTop > 0 && !isScrolling) {
                setIsScrolling(true);
            }
            if (scrollTop === 0 && isScrolling) {
                setIsScrolling(false);
            }
        }
    };
    const handlePageSizeChange = (pageSize) => {
        setInternalPageSize(pageSize);
        table.setPageIndex(0);
        if (!externalPageSize) {
            table.setPageSize(pageSize);
        }
        if (onPageSizeChange) {
            onPageSizeChange(pageSize);
        }
        if (onPageChange) {
            onPageChange(1);
        }
    };
    const handlePageChange = (page) => {
        table.setPageIndex(page - 1);
        if (onPageChange) {
            onPageChange(page);
        }
    };
    const handleActiveFiltersChange = (filters) => {
        setInternalActiveFilters(filters);
        if (onFilterChange) {
            onFilterChange(filters);
        }
        table.setPageIndex(0);
        if (onPageChange) {
            onPageChange(1);
        }
    };
    const handleKeywordFilterChange = (filter) => {
        setInternalKeywordFilter(filter);
        if (onKeywordFilterChange) {
            onKeywordFilterChange(filter);
        }
        table.setPageIndex(0);
        if (onPageChange) {
            onPageChange(1);
        }
    };
    const handleColumnVisibilityChange = (columnVisibility) => {
        setInternalColumnVisibility(columnVisibility);
        if (onColumnVisibilityChange) {
            onColumnVisibilityChange(columnVisibility);
        }
    };
    return (_jsxs("div", { className: "h-full flex flex-col justify-start", children: [_jsx(TableControls, { filters: filters, setColumnFilters: handleActiveFiltersChange, activeFilters: activeFilters, keywordFilter: keywordFilter, setKeywordFilter: hasKeywordFilter ? handleKeywordFilterChange : undefined, keywordFilterLabel: keywordFilterLabel, customControls: customControls, exportOptions: exportOptions, columns: columns, columnVisibility: columnVisibility, showColumnVisibility: showColumnVisibility, setColumnVisibility: handleColumnVisibilityChange }), _jsxs("div", { ref: tableContainerRef, className: classNames("max-h-full overflow-y-auto", isLoading && "relative overflow-hidden h-full"), onScroll: onTableScroll, [testAttribute]: "table-wrapper", children: [_jsxs("table", { className: "w-full overflow-x-auto leading-3 border-collapse bg-background-primary dark:bg-background-primary-darkmode", children: [_jsx("thead", { className: classNames("sticky bg-background-secondary dark:bg-background-secondary-darkmode top-0 z-10", isScrolling && "shadow"), [testAttribute]: "table-head", children: table.getHeaderGroups().map((headerGroup) => (_jsx("tr", { children: headerGroup.headers.map((header) => {
                                        var _a;
                                        return (_jsx(TableHeader, { horizontalAlignment: (_a = header.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.horizontalAlignment, colSpan: header.colSpan, isPlaceholder: header.isPlaceholder, canSort: header.column.getCanSort(), isSortedDirection: header.column.getIsSorted(), onSortChange: header.column.getToggleSortingHandler(), children: flexRender(header.column.columnDef.header, header.getContext()) }, header.id));
                                    }) }, headerGroup.id))) }), _jsx(TableBody, { isSkeleton: isSkeleton, table: table, rowSelection: rowSelection, loadingMessage: t("Loading..."), isEmpty: isEmpty, emptyMessage: emptyMessage })] }), isLoading && !isSkeleton && (_jsxs("div", { className: "absolute top-0 left-0 right-0 bottom-0 flex w-full h-full items-center justify-center", style: { backgroundColor: "rgba(255, 255, 255, 0.5)" }, children: [_jsx(Spinner, {}), _jsx("p", { className: "ml-2", children: t("Loading...") })] }))] }), isPaginated && (_jsx("div", { className: "mt-2", children: _jsx(Pagination, { pageSize: pageSize, pageSizes: [25, 50, 100], currentPage: page !== null && page !== void 0 ? page : table.getState().pagination.pageIndex + 1, totalCount: totalRows !== null && totalRows !== void 0 ? totalRows : table.getFilteredRowModel().rows.length, totalPages: totalPages !== null && totalPages !== void 0 ? totalPages : Math.ceil(table.getFilteredRowModel().rows.length / pageSize), onPageChange: handlePageChange, onPageSizeChange: (pageSize) => handlePageSizeChange(pageSize), isLoading: isLoading }) }))] }));
};
//# sourceMappingURL=Table.js.map